import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Logo } from "../../components/common/Logo";
import { LoginModal } from "../auth/components/LoginModal";
import { RegisterModal } from "../auth/components/RegisterModal";
import { CookieSettingsButton } from "../../components/gdpr";

import { LandingNav } from "./components/LandingNav";
import { HeroVisual } from "./components/HeroVisual";
import { PainStrip } from "./components/PainStrip";
import { PersonaSwitcher } from "./components/PersonaSwitcher";
import { HowItWorks } from "./components/HowItWorks";
import { FeatureBento } from "./components/FeatureBento";
import { IntegrationsRow } from "./components/IntegrationsRow";
import { FAQ } from "./components/FAQ";

const LandingPage = () => {
  const { t } = useTranslation("common");
  const [searchParams, setSearchParams] = useSearchParams();

  const activeModal = searchParams.get("modal");
  const openLogin = () => setSearchParams({ modal: "login" });
  const openRegister = () => setSearchParams({ modal: "register" });
  const closeModal = () => setSearchParams({});

  return (
    <div id="top" className="min-h-screen bg-background-page font-sans selection:bg-accent-primary/20 selection:text-text-primary">
      <LoginModal isOpen={activeModal === "login"} onClose={closeModal} />
      <RegisterModal
        isOpen={activeModal === "register"}
        onClose={closeModal}
        prefillEmail={searchParams.get("email") || undefined}
      />

      <LandingNav onLogin={openLogin} onRegister={openRegister} />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden">
          {/* Subtle grid background (Vercel/Stripe/Linear pattern) */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.35] dark:opacity-[0.25]"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(120,120,120,0.12) 1px, transparent 1px), linear-gradient(to bottom, rgba(120,120,120,0.12) 1px, transparent 1px)",
              backgroundSize: "56px 56px",
              maskImage:
                "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
              WebkitMaskImage:
                "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[1100px] h-[600px] rounded-full blur-3xl opacity-30"
            style={{
              background:
                "radial-gradient(closest-side, rgba(199,97,61,0.35), transparent 70%)",
            }}
          />

          <div className="relative container mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-20 sm:pb-28">
            <div className="max-w-4xl mx-auto text-center mb-14 sm:mb-20">
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-neutral-200 dark:border-neutral-700 bg-white/70 dark:bg-neutral-900/60 text-text-primary text-xs font-medium mb-7"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-accent-primary animate-pulse" />
                {t("landing.heroBadge")}
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.05 }}
                className="text-4xl sm:text-6xl md:text-7xl font-bold font-display text-text-primary tracking-tight leading-[1.05] mb-6"
              >
                {t("landing.heroTitle")}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.12 }}
                className="text-lg sm:text-xl text-neutral-600 dark:text-neutral-300 mb-9 max-w-2xl mx-auto leading-relaxed"
              >
                {t("landing.heroSubtitle")}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.18 }}
                className="flex flex-col sm:flex-row gap-3 justify-center items-center"
              >
                <button
                  onClick={openRegister}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-accent-primary text-white rounded-lg text-base font-semibold hover:bg-accent-primary/90 transition-colors shadow-lg shadow-accent-primary/20"
                >
                  {t("landing.cta.startFree")}
                  <ArrowRight size={18} />
                </button>
                <button
                  onClick={() =>
                    document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="w-full sm:w-auto px-7 py-3.5 bg-white dark:bg-neutral-800 text-text-primary dark:text-white border border-neutral-200 dark:border-neutral-700 rounded-lg text-base font-medium hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
                >
                  {t("landing.cta.howItWorks")}
                </button>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.28 }}
                className="mt-5 text-xs text-neutral-500 dark:text-neutral-400 inline-flex items-center gap-1.5"
              >
                <ShieldCheck size={13} />
                {t("landing.cta.noCard")}
              </motion.p>
            </div>

            <HeroVisual />
          </div>
        </section>

        <PainStrip />
        <HowItWorks />
        <FeatureBento />
        <PersonaSwitcher />
        <IntegrationsRow />
        <FAQ />

        {/* Final CTA */}
        <section className="relative overflow-hidden bg-text-primary dark:bg-neutral-950 py-24 sm:py-32 text-white">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.18]"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.15) 1px, transparent 1px)",
              backgroundSize: "56px 56px",
              maskImage:
                "radial-gradient(ellipse 60% 60% at 50% 50%, black 30%, transparent 75%)",
              WebkitMaskImage:
                "radial-gradient(ellipse 60% 60% at 50% 50%, black 30%, transparent 75%)",
            }}
          />
          <div className="relative container mx-auto px-4 sm:px-6 text-center max-w-3xl">
            <h2 className="text-3xl sm:text-5xl font-bold font-display tracking-tight mb-5">
              {t("landing.finalCta.title")}
            </h2>
            <p className="text-base sm:text-lg text-neutral-300 mb-8 max-w-xl mx-auto">
              {t("landing.finalCta.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={openRegister}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-accent-primary text-white rounded-lg text-base font-semibold hover:bg-accent-primary/90 transition-colors shadow-lg shadow-accent-primary/40"
              >
                {t("landing.cta.startFree")}
                <ArrowRight size={18} />
              </button>
              <button
                onClick={openLogin}
                className="px-7 py-3.5 border border-white/20 rounded-lg text-base font-medium hover:bg-white/5 transition-colors"
              >
                {t("auth.login")}
              </button>
            </div>
            <p className="mt-5 text-xs text-neutral-400 inline-flex items-center gap-1.5">
              <ShieldCheck size={13} />
              {t("landing.cta.noCard")}
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800 py-10">
          <div className="container mx-auto px-4 sm:px-6 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-neutral-500 dark:text-neutral-400">
            <div className="flex items-center gap-2">
              <Logo className="w-5 h-5" />
              <span className="text-lg font-medium font-display text-text-primary dark:text-neutral-100 tracking-tight">
                {t("brandName")}
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-5">
              <a href="/terms" className="hover:text-text-primary transition-colors">
                {t("legal.tos.title")}
              </a>
              <a href="/privacy" className="hover:text-text-primary transition-colors">
                {t("legal.privacy.title")}
              </a>
              <CookieSettingsButton variant="text" />
            </div>
            <div>{t("landing.copyright")}</div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default LandingPage;
